import { ref, computed } from 'vue';
import { config } from '@/config';

// Global auth state
const token = ref(localStorage.getItem('auth_token'));
const isAuthenticated = computed(() => token.value !== null);

// Auth service functions
export const useAuth = () => {
    const login = async (email, password) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            if (response.ok) {
                const data = await response.json();
                token.value = data.token;
                localStorage.setItem('auth_token', data.token);
                return { success: true };
            } else {
                const errorData = await response.json();
                return { 
                    success: false, 
                    error: errorData.error || 'Login failed' 
                };
            }
        } catch (error) {
            return { 
                success: false, 
                error: 'Network error. Please try again.' 
            };
        }
    };

    const logout = async () => {
        try {
            if (token.value) {
                await fetch(`${config.API_BASE_URL}/api/v1/admin/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token.value}`,
                        'Content-Type': 'application/json',
                    },
                });
            }
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            // Always clear local storage regardless of API response
            token.value = null;
            localStorage.removeItem('auth_token');
        }
    };

    const refreshToken = async () => {
        try {
            if (!token.value) return false;

            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/refresh`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token.value}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                token.value = data.token;
                localStorage.setItem('auth_token', data.token);
                return true;
            } else {
                // Token refresh failed, logout user
                await logout();
                return false;
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            await logout();
            return false;
        }
    };

    const getAuthHeaders = () => {
        if (!token.value) return {};
        return {
            'Authorization': `Bearer ${token.value}`,
            'Content-Type': 'application/json',
        };
    };

    // Auto-refresh token before it expires
    const startTokenRefresh = () => {
        // Refresh token every 23 hours (1 hour before expiry)
        setInterval(refreshToken, 23 * 60 * 60 * 1000);
    };

    return {
        token: computed(() => token.value),
        isAuthenticated,
        login,
        logout,
        refreshToken,
        getAuthHeaders,
        startTokenRefresh,
    };
};